// src/hooks/useTaskModals.js
import { useState, useCallback } from "react";
import toast from "react-hot-toast";

export const useTaskModals = ({
  onSaveTask,
  onTaskEdit,
  refreshTasksForJob,
  fetchTasksForJob,
  refreshNotes,
}) => {
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedJobForTask, setSelectedJobForTask] = useState(null);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [selectedJobForTaskEdit, setSelectedJobForTaskEdit] = useState(null);
  const [isUpdatingTask, setIsUpdatingTask] = useState(false);

  const handleCreateTask = useCallback((job) => {
    setSelectedJobForTask({
      id: job.jobId || job.id || job.jobID,
      name: job.jobName,
    });
    setTaskModalOpen(true);
  }, []);

  const closeTaskModal = useCallback(() => {
    setTaskModalOpen(false);
    setSelectedJobForTask(null);
  }, []);

  const closeEditTaskModal = useCallback(() => {
    setEditTaskModalOpen(false);
    setTaskToEdit(null);
    setSelectedJobForTaskEdit(null);
  }, []);

  const handleSaveTask = useCallback(
    async (taskData) => {
      setIsSavingTask(true);
      try {
        if (onSaveTask) {
          await refreshNotes?.();
          await onSaveTask(taskData);
          toast.success(`Task "${taskData.taskName}" created successfully!`);
          closeTaskModal();
          if (selectedJobForTask) {
            await refreshTasksForJob?.(selectedJobForTask.id);
            await fetchTasksForJob?.(selectedJobForTask.id);
          }
        }
      } catch (error) {
        console.error("Failed to save task:", error);
        toast.error(`Failed to create task: ${error.message}`);
      } finally {
        setIsSavingTask(false);
      }
    },
    [onSaveTask, refreshNotes, selectedJobForTask, refreshTasksForJob, fetchTasksForJob, closeTaskModal]
  );

 const handleTaskEdit = useCallback(
  (task, tasksByJob = {}, stackedJobs = []) => {
    let jobForTask = null;

    // Guard against falsy tasksByJob
    if (tasksByJob && typeof tasksByJob === 'object') {
      for (const [jobId, tasks] of Object.entries(tasksByJob)) {
        if (Array.isArray(tasks) && tasks.some((t) => t?.id === task.id)) {
          const job = stackedJobs?.find((j) => j && j.jobId === parseInt(jobId));
          if (job) jobForTask = job;
          break;
        }
      }
    }

    if (!jobForTask && task.jobId && Array.isArray(stackedJobs)) {
      jobForTask = stackedJobs.find(
        (j) => j && (j.jobId === task.jobId || j.id === task.jobId || j.jobID === task.jobId)
      );
    }

    if (jobForTask) {
      setTaskToEdit(task);
      setSelectedJobForTaskEdit({
        id: jobForTask.jobId || jobForTask.id || jobForTask.jobID,
        name: jobForTask.jobName,
      });
      setEditTaskModalOpen(true);
    } else {
      console.error("Could not find job for task:", task);
      toast.error("Could not find the job for this task");
    }
  },
  []
);
  const handleTaskUpdate = useCallback(
    async (updatedTask) => {
      setIsUpdatingTask(true);
      try {
        if (onTaskEdit) {
          await onTaskEdit(updatedTask);
          toast.success("Task updated successfully");
          closeEditTaskModal();
          if (selectedJobForTaskEdit && fetchTasksForJob) {
            await fetchTasksForJob(selectedJobForTaskEdit.id);
          }
        }
      } catch (error) {
        console.error("Failed to update task:", error);
        toast.error(`Failed to update task: ${error.message}`);
      } finally {
        setIsUpdatingTask(false);
      }
    },
    [onTaskEdit, selectedJobForTaskEdit, fetchTasksForJob, closeEditTaskModal]
  );

  return {
    // State
    taskModalOpen,
    selectedJobForTask,
    isSavingTask,
    editTaskModalOpen,
    taskToEdit,
    selectedJobForTaskEdit,
    isUpdatingTask,
    // Handlers
    handleCreateTask,
    handleSaveTask,
    handleTaskEdit,
    handleTaskUpdate,
    // Close functions
    closeTaskModal,
    closeEditTaskModal,
  };
};